import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ALL_REPORT_CORRECTION_IMPACTS,
  REPORT_CORRECTION_IMPACT,
  REPORT_VERSION_STATUS,
  correctionRequiresAffectedResults,
  type ReportCorrectionImpact,
} from '@lims/shared-types';
import {
  openReportCorrectionSchema,
  reportStatusReasonSchema,
  type OpenReportCorrectionInput,
} from '@lims/shared-validation';
import { reportsApi, type ReportDetailView } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  CheckboxField,
  Dialog,
  Panel,
  RadioField,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { isolateRuns, run } from '../settings/report-template-parts.js';
import { attemptDescriber } from './report-draft-actions.js';
import {
  REPORTS_QUERY_KEY,
  ReportNumber,
  ReportRefusalNotice,
  bilingual,
  refusalCallsForReload,
} from './report-parts.js';

/**
 * The acts on a report AFTER issuance — sub-phase 10c.
 *
 * ===========================================================================
 * NONE OF THESE EDITS AN ISSUED DOCUMENT (CLAUDE.md section 8, D118–D120).
 * ===========================================================================
 *
 * A correction opens a replacement DRAFT and changes no result; the version in
 * force stays in force until the replacement is issued. A suspension ends only
 * with a documented decision. A cancellation is terminal, keeps the number, the
 * files and the history, and discards a pending replacement draft with the same
 * reason. Each dialog says so before anybody presses it.
 *
 * WHAT IS SHOWN AFTERWARDS IS WHAT THE SERVER ANSWERED, never what the button
 * implied: the page reloads the report, and the confirmation names the status
 * the report now reads.
 *
 * Every act is offered from the `can.*` hints the server computed for the
 * caller and every route re-checks the permission and the report's state
 * (section 5.1). Hiding a button is presentation, never protection.
 */

/** The shared `reasonSchema`: trimmed, at least 3 characters. */
const MINIMUM_TEXT = 3;
/** `reasonSchema` and a release note allow 2000 characters. */
export const MAXIMUM_REASON = 2000;
/** `requestedChange` allows 4000. */
const MAXIMUM_REQUESTED_CHANGE = 4000;

type TextProblem = 'validation.required' | 'validation.tooShort' | 'validation.tooLong';

/** Why a mandatory free-text answer is not yet acceptable, as a catalogue key — or null. */
export function textProblem(value: string, maximum: number): TextProblem | null {
  const trimmed = value.trim();
  if (trimmed === '') return 'validation.required';
  if (trimmed.length < MINIMUM_TEXT) return 'validation.tooShort';
  if (trimmed.length > maximum) return 'validation.tooLong';
  return null;
}

/**
 * One act on a report.
 *
 * On success the report is read again under the shared key, then `onDone`
 * receives the server's own answer. On a refusal that says the report moved on
 * (a state or conflict refusal), the report is read again too, so the page
 * behind the still-open dialog shows what is really there.
 */
export function useReportAct<TBody>(
  send: (body: TBody) => Promise<ReportDetailView>,
  onDone: (answer: ReportDetailView, body: TBody) => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: async (answer, body) => {
      await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      onDone(answer, body);
    },
    onError: async (error) => {
      if (refusalCallsForReload(error)) {
        await queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY });
      }
    },
  });
}

type ReportVersion = NonNullable<ReportDetailView['currentVersion']>;
type ReportResult = ReportVersion['results'][number];

// ---------------------------------------------------------------------------
// Open a correction
// ---------------------------------------------------------------------------

export function OpenCorrectionDialog({
  report,
  onClose,
}: {
  readonly report: ReportDetailView;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const results = [...(report.currentVersion?.results ?? [])].sort(
    (first, second) => first.sortOrder - second.sortOrder,
  );

  const [reason, setReason] = useState('');
  const [requestedChange, setRequestedChange] = useState('');
  const [impact, setImpact] = useState<ReportCorrectionImpact | null>(null);
  const [affected, setAffected] = useState<string[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  const open = useReportAct(
    (body: OpenReportCorrectionInput) => reportsApi.openCorrection(report.id, body),
    (answer) => {
      show({
        tone: 'success',
        message: t('reports.correction.opened'),
        ...(answer.draftVersion
          ? {
              description: t('reports.correction.openedAs', {
                number: answer.draftVersion.versionNumber,
              }),
            }
          : {}),
      });
      onClose();
    },
  );

  const reasonProblem = textProblem(reason, MAXIMUM_REASON);
  const changeProblem = textProblem(requestedChange, MAXIMUM_REQUESTED_CHANGE);
  const scientific = impact !== null && correctionRequiresAffectedResults(impact);
  // Only results of the CURRENT version, in its print order.
  const affectedInOrder = results
    .map((result) => result.attemptId)
    .filter((attemptId) => affected.includes(attemptId));
  const affectedMissing = scientific && affectedInOrder.length === 0;

  const submit = () => {
    setShowErrors(true);
    if (reasonProblem || changeProblem || impact === null || affectedMissing) return;
    const body: OpenReportCorrectionInput = {
      reason: reason.trim(),
      requestedChange: requestedChange.trim(),
      impact,
      // An administrative correction changes no result, so it names none.
      ...(scientific ? { affectedAttemptIds: affectedInOrder } : {}),
    };
    // The schema the API validates with. A body it refuses is never sent.
    if (!openReportCorrectionSchema.safeParse(body).success) return;
    open.mutate(body);
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="lg"
      title={t('reports.correction.title')}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={open.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton priority="primary" isLoading={open.isPending} onPress={submit}>
            {t('reports.correction.action')}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <Alert
          tone="info"
          title={t('reports.correction.noticeTitle')}
          description={t('reports.correction.noticeBody')}
        />
        <TextAreaField
          label={t('reports.correction.reason')}
          value={reason}
          onChange={setReason}
          isRequired
          rows={3}
          {...(showErrors && reasonProblem ? { error: t(reasonProblem) } : {})}
        />
        <TextAreaField
          label={t('reports.correction.requestedChange')}
          description={t('reports.correction.requestedChangeHint')}
          value={requestedChange}
          onChange={setRequestedChange}
          isRequired
          rows={3}
          {...(showErrors && changeProblem ? { error: t(changeProblem) } : {})}
        />
        <RadioField
          label={t('reports.correction.impact')}
          description={t('reports.correction.impactHint')}
          value={impact}
          onChange={(value) => setImpact(value as ReportCorrectionImpact)}
          isRequired
          options={ALL_REPORT_CORRECTION_IMPACTS.map((value) => ({
            value,
            label: t(`correctionImpact.${value}`),
            description: t(`correctionImpactHint.${value}`),
          }))}
          {...(showErrors && impact === null ? { error: t('validation.required') } : {})}
        />
        {impact === REPORT_CORRECTION_IMPACT.ADMINISTRATIVE ? (
          <p className="text-caption text-default-700">{t('reports.correction.administrativeHint')}</p>
        ) : null}
        {scientific ? (
          <AffectedResults
            results={results}
            selected={affectedInOrder}
            onChange={setAffected}
            {...(showErrors && affectedMissing
              ? { error: t('reports.correction.affectedRequired') }
              : {})}
          />
        ) : null}
        {open.error ? (
          <ReportRefusalNotice error={open.error} describeAttempt={attemptDescriber(results)} />
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * The results a scientific correction names — only those of the version in
 * force. Each is named by its test, its position, its sample and the revision
 * reported; nothing about what it found.
 */
function AffectedResults({
  results,
  selected,
  onChange,
  error,
}: {
  readonly results: readonly ReportResult[];
  readonly selected: readonly string[];
  readonly onChange: (attemptIds: string[]) => void;
  /** Already translated. */
  readonly error?: string;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const heading = useId();

  const toggle = (attemptId: string, include: boolean) =>
    onChange(
      include
        ? [...selected.filter((id) => id !== attemptId), attemptId]
        : selected.filter((id) => id !== attemptId),
    );

  return (
    <section className="flex flex-col gap-stack" aria-labelledby={heading}>
      <div>
        <h3 id={heading} className="text-body font-medium text-default-900">
          {t('reports.correction.affected')}
        </h3>
        <p className="mt-1 text-caption text-default-600">{t('reports.correction.affectedHint')}</p>
      </div>
      <ul className="flex flex-col divide-y divide-default-100" data-testid="affected-results">
        {results.map((result, index) => (
          <li
            key={result.attemptId}
            data-attempt={result.attemptId}
            className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0"
          >
            <CheckboxField
              label={bilingual(result.test.name, locale) ?? t('common.notRecorded')}
              value={selected.includes(result.attemptId)}
              onChange={(include) => toggle(result.attemptId, include)}
            />
            <p className="text-caption text-default-600">
              <span className="tabular-nums">{index + 1}</span>
              {' · '}
              {t('reports.detail.resultColumns.sample')}
              {': '}
              <span className="measure" dir="ltr">
                {result.sample.number}
              </span>
              {' · '}
              {t('reports.detail.resultColumns.revision')}
              {': '}
              <span className="tabular-nums">{result.revisionNumber}</span>
            </p>
          </li>
        ))}
      </ul>
      {error ? (
        <p role="alert" className="text-caption text-danger-700">
          {error}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Suspend, reinstate, cancel — each with its reason, each recorded
// ---------------------------------------------------------------------------

export type StatusAct = 'suspend' | 'reinstate' | 'cancel';

export function StatusReasonDialog({
  report,
  act,
  onClose,
}: {
  readonly report: ReportDetailView;
  readonly act: StatusAct;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const problem = textProblem(reason, MAXIMUM_REASON);
  // Only a cancellation reaches the draft: a pending replacement is discarded with it.
  const pendingDraft = act === 'cancel' ? report.draftVersion : null;

  const send = (body: { reason: string }) =>
    act === 'suspend'
      ? reportsApi.suspend(report.id, body)
      : act === 'reinstate'
        ? reportsApi.reinstate(report.id, body)
        : reportsApi.cancel(report.id, body);

  const change = useReportAct(send, (answer) => {
    const draftDiscarded =
      pendingDraft !== null &&
      answer.versions.some(
        (version) =>
          version.id === pendingDraft.id && version.status === REPORT_VERSION_STATUS.DISCARDED,
      );
    show({
      tone: 'success',
      message:
        act === 'suspend'
          ? t('reports.suspend.done')
          : act === 'reinstate'
            ? t('reports.reinstate.done')
            : draftDiscarded
              ? t('reports.cancel.doneWithDraft')
              : t('reports.cancel.done'),
      // What the server says the report now is — not what the button implied.
      description: t('reports.lifecycle.statusNow', {
        status: t(`status.report.${answer.status}`),
      }),
    });
    onClose();
  });

  const submit = () => {
    setTouched(true);
    const body = { reason: reason.trim() };
    // The schema the API validates with. A body it refuses is never sent.
    if (problem || !reportStatusReasonSchema.safeParse(body).success) return;
    change.mutate(body);
  };

  const title =
    act === 'suspend'
      ? t('reports.suspend.action')
      : act === 'reinstate'
        ? t('reports.reinstate.action')
        : t('reports.cancel.action');
  const reasonLabel =
    act === 'suspend'
      ? t('reports.suspend.reason')
      : act === 'reinstate'
        ? t('reports.reinstate.reason')
        : t('reports.cancel.reason');

  return (
    <Dialog
      isOpen
      onClose={onClose}
      size="lg"
      title={title}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={change.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority={act === 'cancel' ? 'destructive' : 'primary'}
            // Mandatory here AND on the server, which refuses the act without one.
            isDisabled={problem !== null}
            isLoading={change.isPending}
            onPress={submit}
          >
            {title}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        {act === 'suspend' ? (
          <>
            <Alert
              tone="warning"
              title={t('reports.suspend.noticeTitle')}
              description={t('reports.suspend.noticeBody')}
            />
            <p className="text-caption text-default-700">{t('reports.suspend.staffStillHint')}</p>
          </>
        ) : null}
        {act === 'reinstate' ? (
          <Alert
            tone="info"
            title={t('reports.reinstate.noticeTitle')}
            description={t('reports.reinstate.noticeBody')}
          />
        ) : null}
        {act === 'cancel' ? (
          <>
            <Alert
              tone="warning"
              title={t('reports.cancel.noticeTitle')}
              description={t('reports.cancel.noticeBody')}
            />
            {/* Said plainly, with the number in front of the person cancelling. */}
            <p className="text-body text-default-800" data-testid="number-kept">
              {isolateRuns(t('reports.cancel.numberKept', { number: run('number') }), {
                number: <ReportNumber number={report.reportNumber} />,
              })}
            </p>
            {pendingDraft ? (
              <Alert
                tone="warning"
                title={t('reports.cancel.pendingDraft', { number: pendingDraft.versionNumber })}
              />
            ) : null}
            <p className="text-caption text-default-700">{t('reports.cancel.publicNote')}</p>
          </>
        ) : null}
        <TextAreaField
          label={reasonLabel}
          {...(act === 'reinstate' ? { description: t('reports.reinstate.reasonHint') } : {})}
          value={reason}
          onChange={(value) => {
            setReason(value);
            setTouched(true);
          }}
          isRequired
          rows={3}
          {...(touched && problem ? { error: t(problem) } : {})}
        />
        {change.error ? <ReportRefusalNotice error={change.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// The acts, offered from the server's hints
// ---------------------------------------------------------------------------

/**
 * Correct, suspend, reinstate, cancel — each offered only where the server's
 * hint says the caller may, and each re-checked by the server.
 *
 * The open dialog is held HERE, outside the hint check: if the reload after a
 * refusal turns a hint off, the dialog stays open with the refusal in words
 * instead of vanishing with the button.
 */
export function LifecycleActions({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<'correct' | StatusAct | null>(null);
  const { can } = report;
  const offered = can.correct || can.suspend || can.reinstate || can.cancel;
  const close = () => setDialog(null);

  return (
    <>
      {offered ? (
        <Panel title={t('reports.lifecycle.title')} description={t('reports.lifecycle.hint')}>
          <div className="flex flex-wrap items-center gap-inline" data-testid="lifecycle-actions">
            {can.correct ? (
              <ActionButton priority="secondary" size="sm" onPress={() => setDialog('correct')}>
                {t('reports.correction.action')}
              </ActionButton>
            ) : null}
            {can.suspend ? (
              <ActionButton priority="secondary" size="sm" onPress={() => setDialog('suspend')}>
                {t('reports.suspend.action')}
              </ActionButton>
            ) : null}
            {can.reinstate ? (
              <ActionButton priority="secondary" size="sm" onPress={() => setDialog('reinstate')}>
                {t('reports.reinstate.action')}
              </ActionButton>
            ) : null}
            {can.cancel ? (
              <ActionButton priority="destructive" size="sm" onPress={() => setDialog('cancel')}>
                {t('reports.cancel.action')}
              </ActionButton>
            ) : null}
          </div>
        </Panel>
      ) : null}
      {dialog === 'correct' ? <OpenCorrectionDialog report={report} onClose={close} /> : null}
      {dialog === 'suspend' || dialog === 'reinstate' || dialog === 'cancel' ? (
        <StatusReasonDialog key={dialog} report={report} act={dialog} onClose={close} />
      ) : null}
    </>
  );
}
